/**
 * 微信授权工具类
 * 用于静默获取微信openid
 */
import config from '@/config'
import { getWxAuthUrl } from '@/api/user'

class WechatAuth {
  constructor() {
    this.appId = config.wechat.appId
    this.useBackendAuthUrl = true // 是否使用后端接口生成授权链接（推荐，更安全）
  }

  /**
   * 检查URL中是否包含微信授权code
   * 注意：即使URL中有hash（如 #/login），也能正确提取query参数
   * @returns {string|null} 返回code或null
   */
  getCodeFromUrl() {
    // 使用 window.location.search 可以正确获取 query 参数，即使有 hash 也不影响
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    console.log('🔍 getCodeFromUrl():', {
      '当前URL': window.location.href,
      'search': window.location.search,
      'hash': window.location.hash,
      'code': code
    })
    return code
  }

  /**
   * 检查URL中是否包含state参数
   * 注意：即使URL中有hash（如 #/login），也能正确提取query参数
   * @returns {string|null}
   */
  getStateFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const state = urlParams.get('state')
    console.log('🔍 getStateFromUrl():', {
      'search': window.location.search,
      'state': state
    })
    return state
  }

  /**
   * 生成随机state字符串
   * @returns {string}
   */
  generateState() {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15)
  }

  /**
   * 跳转到微信授权页面（静默授权）
   * @param {string} redirectUri 授权后重定向的回调链接地址
   * @param {string} state 可选，state参数
   * @param {string} accountType 账号类型：DEFAULT、XHT_PUBLIC、HZ，默认为XHT_PUBLIC
   */
  async redirectToWechatAuth(redirectUri, state = null, accountType = 'XHT_PUBLIC') {
    if (!state) {
      state = this.generateState()
    }
    // 保存state到sessionStorage，用于验证
    sessionStorage.setItem('wechat_auth_state', state)

    let wechatAuthUrl

    if (this.useBackendAuthUrl) {
      // 使用后端接口生成授权链接（推荐，确保使用正确的配置）
      try {
        console.log('🔄 通过后端接口获取微信授权链接...')
        const response = await getWxAuthUrl(redirectUri, accountType, true, state)
        wechatAuthUrl = response?.data?.authUrl || response?.authUrl
        
        if (!wechatAuthUrl) {
          console.error('❌ 后端返回的授权链接为空，使用前端生成方式')
          throw new Error('后端返回的授权链接为空')
        }
        console.log('✅ 后端返回的授权链接:', wechatAuthUrl)
      } catch (error) {
        console.error('❌ 通过后端获取授权链接失败，使用前端生成方式:', error)
        // 降级到前端生成方式
        wechatAuthUrl = this.generateAuthUrlFrontend(redirectUri, state)
      }
    } else {
      // 前端直接生成授权链接
      wechatAuthUrl = this.generateAuthUrlFrontend(redirectUri, state)
    }

    console.log('🔄 跳转到微信授权页面:', wechatAuthUrl)
    window.location.href = wechatAuthUrl
  }

  /**
   * 前端生成授权链接（备用方案）
   * @param {string} redirectUri 授权后重定向的回调链接地址
   * @param {string} state state参数
   * @returns {string} 授权链接
   */
  generateAuthUrlFrontend(redirectUri, state) {
    const encodedRedirectUri = encodeURIComponent(redirectUri)
    return `https://open.weixin.qq.com/connect/oauth2/authorize?` +
        `appid=${this.appId}&` +
        `redirect_uri=${encodedRedirectUri}&` +
        `response_type=code&` +
        `scope=snsapi_base&` +  // 静默授权，仅获取openid
        `state=${state}#wechat_redirect`
  }

  /**
   * 处理微信授权回调
   * @returns {Promise<{code: string, state: string}>}
   */
  async handleAuthCallback() {
    console.log('🔄 handleAuthCallback() 开始执行')
    console.log('📍 当前URL:', window.location.href)
    console.log('📍 window.location.search:', window.location.search)
    
    const code = this.getCodeFromUrl()
    const state = this.getStateFromUrl()

    console.log('🔍 handleAuthCallback() 解析结果:', {
      'code': code,
      'state': state,
      'hasCode': !!code
    })

    if (!code) {
      console.log('⚠️ handleAuthCallback() 未找到code，返回null')
      return null
    }

    // 验证state（如果state是业务标识如'XHT_PUBLIC'，则不需要验证sessionStorage）
    // 如果state是业务标识（如'XHT_PUBLIC', 'HZ'），直接使用
    // 如果state是随机生成的，需要验证sessionStorage
    const savedState = sessionStorage.getItem('wechat_auth_state')
    const isBusinessState = state === 'XHT_PUBLIC' || state === 'HZ' || state === 'DEFAULT'
    
    console.log('🔍 State验证:', {
      'savedState': savedState,
      'currentState': state,
      'isBusinessState': isBusinessState,
      'needVerify': !!savedState && !isBusinessState
    })
    
    if (savedState && !isBusinessState && state !== savedState) {
      console.error('⚠️ State验证失败，可能存在CSRF攻击')
      console.error('  - savedState:', savedState)
      console.error('  - currentState:', state)
      return null
    }

    // 清除sessionStorage中的state（如果存在且不是业务标识）
    if (savedState && !isBusinessState) {
      console.log('🧹 清除sessionStorage中的state')
      sessionStorage.removeItem('wechat_auth_state')
    }

    // 清除URL中的code和state参数
    console.log('🧹 准备清除URL中的code和state参数')
    this.cleanUrlParams()
    console.log('📍 清除后的URL:', window.location.href)

    console.log('✅ handleAuthCallback() 返回:', { code, state })
    return { code, state }
  }

  /**
   * 清除URL中的授权参数
   * 注意：保留hash部分（如 #/login），只清除query参数
   */
  cleanUrlParams() {
    const url = new URL(window.location.href)
    const hash = window.location.hash // 保存hash部分
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    // 重新构建URL，保留hash
    const newUrl = url.toString() + (hash || '')
    window.history.replaceState({}, '', newUrl)
    console.log('🧹 清除URL参数后:', newUrl)
  }

  /**
   * 检查是否在微信环境中
   * @returns {boolean}
   */
  isWechatBrowser() {
    const ua = navigator.userAgent.toLowerCase()
    return ua.indexOf('micromessenger') !== -1
  }
}

// 创建全局实例
const wechatAuth = new WechatAuth()

export default wechatAuth

